import axios, { AxiosRequestConfig, Method } from 'axios';
import { Auth } from './cognito';
import { warn, log } from './log';
import { User, UserRole } from '../types/user';
import { Account } from '../types/account';
import { Manager } from '../types/manager';
import { Receipt, DocumentFile } from '../types/receipt';
import { Document } from '../types/document';
import { Category } from '../types/category';
import { Period, PeriodStatus } from '../types/period';
import { AccountProfile } from '../pages/accounts/accountProfile';
import { toast } from 'react-toastify';

const LOG_CHANNEL = 'api';

interface ApiOptions {
  API_URL?: string;
  FILES_URL?: string;
}

export interface ItemCount {
  count: number;
}

class ApiClient {
  API_URL: string | undefined;
  FILES_URL: string | undefined;
  constructor({ API_URL, FILES_URL }: ApiOptions) {
    log(LOG_CHANNEL, 'init api', API_URL, FILES_URL);
    this.API_URL = API_URL;
    this.FILES_URL = FILES_URL;
  }
  getDashboardChart({
    year,
    managerId,
    accountId,
  }: {
    year: number;
    managerId?: string;
    accountId?: string;
  }) {
    if (managerId) {
      return this.get(`stats/receipts?year=${year}&managerId=${managerId}`);
    } else if (accountId) {
      return this.get(`stats/receipts?year=${year}&accountId=${accountId}`);
    } else return this.get(`stats/receipts?year=${year}`);
  }
  getDashboardTable({
    year,
    managerId,
    accountId,
  }: {
    year: number;
    managerId?: string;
    accountId?: string;
  }) {
    if (managerId) {
      return this.get(`stats/accounts?year=${year}&managerId=${managerId}`);
    } else if (accountId) {
      return this.get(`stats/accounts/${accountId}?year=${year}`);
    } else return this.get(`stats/managers/?year=${year}`);
  }
  getStatus() {
    return this.get('status');
  }
  getUser(userId: number | string) {
    return this.get(`users/${userId}`);
  }
  updateUser(userId: number | string, { email }: { email: string }) {
    return this.post(`users/${userId}`, { email });
  }
  enableUser(userId: number | string, accountId?: string): Promise<User> {
    return this.post(`users/${userId}/enable-account/${accountId}`);
  }
  disableUser(userId: number | string, accountId?: string): Promise<User> {
    return this.post(`users/${userId}/disable-account/${accountId}`);
  }
  disableManagerUser(
    userId: number | string,
    managerId?: string,
  ): Promise<User> {
    return this.post(`users/${userId}/disable-manager/${managerId}`);
  }

  enableManagerUser(
    userId: number | string,
    managerId?: string,
  ): Promise<User> {
    return this.post(`users/${userId}/enable-manager/${managerId}`);
  }

  getUsers({
    accountId,
    managerId,
  }: {
    accountId?: string;
    managerId?: string;
  }): Promise<User[]> {
    return this.get('users', { accountId, managerId });
  }
  createUser({
    email,
    role,
    accountId,
    managerId,
  }: {
    email: string;
    role: UserRole;
    accountId?: string;
    managerId?: string;
  }): Promise<User> {
    return this.post('users', { email, role, accountId, managerId });
  }
  createAccount({ name }: { name: string }): Promise<Account> {
    return this.post('accounts', { name });
  }
  getAccounts({ s }: { s?: string }): Promise<Account[]> {
    return this.get('accounts', { s });
  }
  getAccount(accountId: string): Promise<Account> {
    return this.get(`accounts/${accountId}`);
  }
  updateAccount(account: Account): Promise<Account> {
    return this.post(`accounts/${account.id}`, account);
  }
  updateAccountProfile(
    accountId: string,
    accountProfile: AccountProfile,
  ): Promise<AccountProfile> {
    return this.post(`accounts/${accountId}/profile`, accountProfile);
  }
  resetInboundPassword(accountId: string): Promise<Account> {
    return this.post(`accounts/${accountId}/settings/resetinboundpassword`);
  }
  resetUserPassword(user: User, accountId: string) {
    return this.post(`users/${user.id}/resetpassword/${accountId}`);
  }

  deleteUserManager({ user, managerId }: { managerId?: string; user: User }) {
    return this.post(`users/${user.id}/remove-manager/${managerId}`);
  }

  deleteUser({ user, accountId }: { user: User; accountId?: string }) {
    return this.post(`users/${user.id}/remove-account/${accountId}`);
  }
  createManager({ name }: { name?: string }): Promise<Manager> {
    return this.post(`managers/`, { name });
  }
  getManager({ managerId }: { managerId?: string }): Promise<Manager> {
    return this.get(`managers/${managerId}`);
  }
  getManagers(): Promise<Manager[]> {
    return this.get('managers');
  }

  updateManager(manager: Manager): Promise<Manager> {
    return this.post(`managers/${manager.id}`, manager);
  }

  createReceipt({
    name,
    accountId,
    categorySlug,
    periodSlug,
  }: {
    name: string;
    accountId: number | string;
    categorySlug?: string;
    periodSlug?: string;
  }): Promise<Receipt> {
    return this.post('receipts', { name, accountId, categorySlug, periodSlug });
  }
  updateReceipt(
    receiptId: number | string,
    data: {
      previewFile?: DocumentFile;
      originalFile?: DocumentFile;
      category?: Category;
      period?: Period;
    },
  ): Promise<Receipt> {
    return this.put(`receipts/${receiptId}`, data);
  }
  archiveReceipt(receiptId: number | string) {
    return this.post(`receipts/${receiptId}/archive`);
  }
  getReceipt(receiptId: number | string): Promise<Receipt> {
    return this.get(`receipts/${receiptId}`);
  }
  getPeriods({
    year,
    accountId,
  }: {
    year?: string;
    accountId?: string;
  }): Promise<Period[]> {
    return this.get(`receipts/periods`, { year, accountId });
  }
  zipPeriod(periodId: number | string): Promise<Period> {
    return this.post(`receipts/periods/${periodId}/zip`);
  }
  zipAllPeriods(accountId: number | string): Promise<string> {
    return this.post(`receipts/periods/zip?accountId=${accountId}`);
  }

  // updatePeriodBySlug(periodSlug:string, period:{status?:PeriodStatus}):Promise<Period>{
  //     return this.put(`receipts/periods/${periodSlug}`,period)
  // }
  updatePeriod(
    periodId: string,
    { period }: { period: Period },
  ): Promise<Period> {
    return this.put(`receipts/periods/${periodId}`, period);
  }
  getReceipts({
    accountId,
    category,
    period,
    year,
    unsorted,
  }: {
    accountId: string;
    category?: string;
    period?: string;
    year?: string;
    unsorted?: boolean;
  }): Promise<Receipt[]> {
    return this.get('receipts', {
      accountId,
      category,
      period,
      year,
      unsorted,
    });
  }
  getUnsortedCount({ accountId }: { accountId: string }): Promise<ItemCount> {
    return this.get('receipts/count/unsorted', { accountId });
  }
  getDocuments({ accountId }: { accountId?: string }): Promise<Document[]> {
    return this.get('documents', { accountId });
  }
  createDocument({
    name,
    accountId,
  }: {
    name: string;
    accountId: number | string;
  }): Promise<Document> {
    return this.post('documents', { name, accountId });
  }
  updateDocument(
    documentId: number | string,
    data: { previewFile?: DocumentFile; originalFile?: DocumentFile },
  ): Promise<Document> {
    return this.put(`documents/${documentId}`, data);
  }
  archiveDocument(documentId: number | string) {
    return this.post(`documents/${documentId}/archive`);
  }
  getCategories({ accountId }: { accountId?: string }): Promise<Category[]> {
    return this.get(`categories`, { accountId });
  }
  createCategory({
    name,
    accountId,
  }: {
    name: string;
    accountId: number | string;
  }): Promise<Category> {
    return this.post('categories', { name, accountId });
  }
  archiveCategory({
    categoryId,
  }: {
    categoryId: number | string;
  }): Promise<Category> {
    return this.put(`categories/${categoryId}/archive`, {});
  }
  updateCategory(
    categoryId: number | string,
    data: { name: string; order?: number },
  ): Promise<Category> {
    return this.put(`categories/${categoryId}`, data);
  }
  get(endpoint: string, params?: object) {
    // TODO; fixe typing
    return this.request('get', endpoint, { params });
  }
  post(endpoint: string, data?: object) {
    return this.request('post', endpoint, { data });
  }
  put(endpoint: string, data?: object) {
    return this.request('put', endpoint, { data });
  }
  async importFile({
    file,
    accountId,
    periodId,
    categoryId,
  }: {
    file: File;
    accountId: string;
    periodId: string;
    categoryId: string;
  }): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('accountId', accountId);
    formData.append('periodId', periodId);
    formData.append('categoryId', categoryId);
    return this.post('receipts/import/csv', formData);
  }
  async uploadFile(
    { file, accountId, type }: { file?: File; accountId: string; type: string },
    config: AxiosRequestConfig,
  ): Promise<{ file: DocumentFile; start: () => void } | null> {
    if (!file) {
      warn(LOG_CHANNEL, 'no file provided');
      return null;
    }
    const token = await Auth.getJwtToken();
    if (!token) {
      warn(LOG_CHANNEL, 'no auth token');
      return null;
    }

    const res = await axios.post<{ files: DocumentFile[] }>(
      `${this.FILES_URL}upload`,
      { names: [file.name], accountId, type },
      {
        headers: {
          authorization: token,
        },
      },
    );
    // console.log('response', res.data)
    const uploadedFile = res.data.files[0];
    config.responseType = 'text'; // needed to prevent xml parsing error in FF
    const { uploadUrl } = uploadedFile;
    console.log('got upload url', uploadUrl);
    if (!uploadUrl) {
      warn(LOG_CHANNEL, 'no upload url created');
      return null;
    }
    const start = async () => {
      console.log('start upload', config, uploadUrl);
      try {
        const res = await axios.put(uploadUrl, file, config);
        return res;
      } catch (e) {
        console.log('upload error', e);
      }
      console.log('finished putting file', res.data);
    };
    return { file: uploadedFile, start };
  }
  async request(
    method: Method,
    endpoint: string,
    {
      data,
      params,
      formData,
    }: { data?: object; params?: object; formData?: FormData },
  ) {
    try {
      const token = await Auth.getJwtToken();
      if (!token) {
        return warn(LOG_CHANNEL, 'no auth token');
      }
      const payload = {
        method,
        url: `${this.API_URL}${endpoint}`,
        params,
        data: formData ? formData : data,
        headers: {
          authorization: token,
          'Content-Type': formData ? 'multipart/form-data' : undefined,
        },
      };
      const res = await axios(payload);

      return res.data;
    } catch (error: any) {
      let message = error.message;
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        message = error.response.data.message;
        toast.error(message, { autoClose: 3000 });
        return new Error(message);
      }
      toast.error('Es ist leider ein Fehler aufgetreten', { autoClose: 3000 });
    }
  }
}

export let Api: ApiClient;
export const initApi = (options: ApiOptions) => {
  // api should be a singleton
  if (!Api) Api = new ApiClient(options);
  return Api;
};
